import React from "react";
import { createPortal } from "react-dom";
import ExpandableZoomableImage from "components/ExpandableZoomableImage";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type FigureLegend = Record<string, string>;

export interface FeatureColumnData {
  /** annotation_id when available, else "<m|n>-<bucketIndex>" */
  key: string;
  subFeature: string;
  isMatched: boolean;
  isCurrent: boolean;
  hexCode?: string;
  patentFigureUrl?: string;
  figureNumber?: string;
  figureLegend?: FigureLegend[];
  accusedImageUrl?: string;
  /** Single-feature claim-chart table (pre-built HTML). */
  chartHtml?: string;
  /** DSPy text analysis (citations [n] converted to sups by the parent walker). */
  analysisHtml?: string;
  figureInsightHtml?: string;
  matchPercentage?: string | number;
}

interface PerFeatureAnalysisViewProps {
  claimLabel: string;
  clauseHtml: string;
  /** One table for the whole element — each feature's row from its own item. */
  commonChartHtml?: string;
  /** Analysis text of the item at hand only. */
  currentAnalysisHtml?: string;
  currentSubFeature?: string;
  columns: FeatureColumnData[];
  /** Key of the column expanded to the 80% modal (null = none). */
  expandedKey?: string | null;
  onToggleExpand?: (key: string | null) => void;
  onSelectColumn: (key: string) => void;
  onChangePatentImage: (key: string) => void;
  onAnnotate: (key: string) => void;
  onReanalyze: (key: string) => void;
  /** Source patent document link ("View Patent" beside the figure header). */
  patentLink?: string;
  /** Target document link ("View Target" beside the accused header). */
  targetLink?: string;
  /** Presigned link to the saved PDF snapshot of a website target. */
  targetSnapshotLink?: string;
  /** Fired after a rotate-and-save so the parent can sync duplicated surfaces. */
  onImageRotated?: (newUrl: string, oldUrl: string) => void;
}

const CameraIcon = ({ size = 40, stroke = "#98A2B3" }: { size?: number; stroke?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <circle cx="8.5" cy="8.5" r="1.5"></circle>
    <polyline points="21 15 16 10 5 21"></polyline>
  </svg>
);

const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
  </svg>
);

// Spreadsheet-style column-resize glyph (vertical bar + left/right arrows) as
// a custom CURSOR — it follows the mouse along the edge instead of rendering
// at a fixed spot. White halo underneath keeps it readable on any background.
const COL_RESIZE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="white" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="4" x2="12" y2="20"/><polyline points="8 8 4 12 8 16"/><line x1="4" y1="12" x2="9" y2="12"/><polyline points="16 8 20 12 16 16"/><line x1="15" y1="12" x2="20" y2="12"/></g><g fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="4" x2="12" y2="20"/><polyline points="8 8 4 12 8 16"/><line x1="4" y1="12" x2="9" y2="12"/><polyline points="16 8 20 12 16 16"/><line x1="15" y1="12" x2="20" y2="12"/></g></svg>`;
const COL_RESIZE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  COL_RESIZE_SVG
)}") 12 12, col-resize`;

const NoPreview = ({ height = "14rem" }: { height?: string }) => (
  <div
    className="w-full bg-[#F2F4F7] border-2 border-dashed border-[#D0D5DD] rounded-lg flex flex-col items-center justify-center gap-2.5"
    style={{ height }}
  >
    <CameraIcon />
    <span className="text-sm text-[#667085] font-interMedium">No Preview Available</span>
  </div>
);

const actionBtnCls =
  "flex items-center gap-1 px-2.5 py-1 text-xs text-white rounded";
const actionBtnStyle: React.CSSProperties = {
  backgroundColor: "#1751D0",
  border: "none",
  cursor: "pointer",
};

/**
 * Per-feature "View More Details": one column per sub-feature of the clicked
 * item's element clause. The current item's column shows its own analysis;
 * sibling columns are assembled from the siblings' own stored items (own
 * patent figure x own accused image x own verdict) — nothing is recomputed.
 */
const PerFeatureAnalysisView: React.FC<PerFeatureAnalysisViewProps> = ({
  claimLabel,
  clauseHtml,
  commonChartHtml,
  currentAnalysisHtml,
  currentSubFeature,
  columns,
  expandedKey,
  onToggleExpand,
  onSelectColumn,
  onChangePatentImage,
  onAnnotate,
  onReanalyze,
  patentLink,
  targetLink,
  targetSnapshotLink,
  onImageRotated,
}) => {
  // Small "View Patent" / "View Target" link rendered beside the section
  // captions; the target may be a website, a file, or another patent, so the
  // labels stay format-agnostic.
  const sourceLink = (href: string | undefined, label: string) =>
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-0.5 text-[10px] font-interSemiBold text-[#1751D0] hover:underline whitespace-nowrap flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        {label}
        <OpenInNewIcon sx={{ fontSize: 11 }} />
      </a>
    ) : null;
  const n = columns.length;

  // Columns render as a single horizontal strip. When they overflow the
  // viewport the strip scrolls (snap + chevron paddles + edge fades); when
  // they fit it just centers them.
  const stripRef = React.useRef<HTMLDivElement | null>(null);
  const [strip, setStrip] = React.useState({
    overflowing: false,
    canLeft: false,
    canRight: false,
  });
  // Per-card width, resizable by dragging the card's right edge.
  const [colWidths, setColWidths] = React.useState<Record<string, number>>({});
  // Live width during a drag: a mid-drag re-render (e.g. the scroll paddles
  // flipping from the drag's own scrollLeft writes) must NOT re-apply the
  // stale state width — that snapped the card back and made left drags jerky.
  const dragWidthRef = React.useRef<Record<string, number>>({});
  const draggingRef = React.useRef(false);

  const updateStrip = React.useCallback(() => {
    // No paddle-state churn (→ re-renders) while a card drag is in flight
    if (draggingRef.current) return;
    const el = stripRef.current;
    if (!el) return;
    const overflowing = el.scrollWidth > el.clientWidth + 4;
    setStrip((prev) => {
      const next = {
        overflowing,
        canLeft: overflowing && el.scrollLeft > 4,
        canRight:
          overflowing && el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      };
      return prev.overflowing === next.overflowing &&
        prev.canLeft === next.canLeft &&
        prev.canRight === next.canRight
        ? prev
        : next;
    });
  }, []);

  React.useEffect(() => {
    updateStrip();
    window.addEventListener("resize", updateStrip);
    return () => window.removeEventListener("resize", updateStrip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateStrip, columns, colWidths]);

  // Keep the selected feature's column in view (pill clicks, context switches).
  const currentKey = columns.find((c) => c.isCurrent)?.key;
  React.useEffect(() => {
    if (!currentKey || !stripRef.current) return;
    const el = stripRef.current.querySelector<HTMLElement>(
      `[data-col-key="${CSS.escape(currentKey)}"]`
    );
    el?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [currentKey]);

  const scrollStrip = (dir: 1 | -1) =>
    stripRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });

  const expandedCol = expandedKey
    ? columns.find((c) => c.key === expandedKey)
    : undefined;

  const startCardResize = (
    e: React.MouseEvent,
    key: string,
    edge: "left" | "right" = "right"
  ) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    const startX = e.clientX;
    const startW = colWidths[key] || 330;
    let prevW = startW;
    // Resize the DOM synchronously during the drag: the scroll compensation
    // for left-edge drags needs the new width applied in the same frame,
    // otherwise scrollLeft clamps and the card only ever grows rightward.
    const colEl = stripRef.current?.querySelector<HTMLElement>(
      `[data-col-key="${CSS.escape(key)}"]`
    );
    // Scroll anchoring fights the manual scrollLeft writes — suspend it.
    // (Scroll-snap is off entirely: restoring it after a left drag re-snapped
    // to a card boundary, undoing the leftward growth in one jump.)
    const stripEl = stripRef.current;
    if (stripEl) stripEl.style.overflowAnchor = "none";
    const scroll0 = stripEl?.scrollLeft ?? 0;
    // One apply per animation frame: mousemove fires several times per frame,
    // and each scrollLeft write forces a synchronous reflow — unthrottled
    // that's multiple layouts per frame and a visibly jerky left drag.
    let raf = 0;
    let targetW = startW;
    const apply = () => {
      raf = 0;
      if (targetW === prevW) return;
      dragWidthRef.current[key] = targetW;
      if (colEl) colEl.style.width = `${targetW}px`;
      // Left-edge drag: keep the right edge visually pinned by scrolling the
      // strip along with the width change, so the card grows leftward.
      // Absolute target (anchored to the drag-start scroll) — incremental +=
      // drifts whenever a frame's scroll gets clamped or rounded.
      if (edge === "left" && stripEl) {
        stripEl.scrollLeft = scroll0 + (targetW - startW);
      }
      prevW = targetW;
    };
    const onMove = (ev: MouseEvent) => {
      const delta =
        edge === "right" ? ev.clientX - startX : startX - ev.clientX;
      targetW = Math.min(760, Math.max(260, startW + delta));
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      if (raf) cancelAnimationFrame(raf);
      apply();
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      if (stripEl) stripEl.style.overflowAnchor = "";
      draggingRef.current = false;
      setColWidths((prev) => ({ ...prev, [key]: prevW }));
      delete dragWidthRef.current[key];
      updateStrip();
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = COL_RESIZE_CURSOR;
    document.body.style.userSelect = "none";
  };

  const patentSection = (col: FeatureColumnData, imgHeight: string) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Two rows so the caption, links, and action buttons never fight for
          one line on narrow cards: caption + links up top, buttons below. */}
      <div className="px-2.5 py-1.5 bg-[#F8FAFC] border-b border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
          <span className="text-[10px] font-interSemiBold tracking-wide uppercase text-[#667085] whitespace-nowrap">
            Patent Figure
          </span>
          {sourceLink(patentLink, "View Patent")}
        </div>
        <div className="flex gap-1.5 mt-1.5">
          <button
            type="button"
            onClick={() => onChangePatentImage(col.key)}
            className={actionBtnCls}
            style={actionBtnStyle}
          >
            <CameraIcon size={12} stroke="currentColor" />
            Change Image
          </button>
        </div>
      </div>
      <div className="bg-[#FAFBFC]">
        {col.patentFigureUrl ? (
          /* Image | legend side by side — same manner as the Claim Figures
             panel of the claim view ("10. ear" numbered list beside the
             figure). Legend column only when there is legend data. */
          <div className="flex">
            <div className="flex-1 min-w-0">
              <ExpandableZoomableImage
                url={col.patentFigureUrl}
                alt={`Patent figure — ${col.subFeature}`}
                title={`Patent Figure — ${col.subFeature}`}
                height={imgHeight}
                link={patentLink}
                linkLabel="View Patent"
                legend={col.figureLegend}
                onRotated={onImageRotated}
              />
            </div>
            {col.figureLegend && col.figureLegend.length > 0 && (
              <ul
                className="w-[38%] flex-shrink-0 overflow-y-auto space-y-1 px-2.5 py-2 border-l border-dashed border-gray-200 [scrollbar-width:thin]"
                style={{ maxHeight: imgHeight }}
              >
                {col.figureLegend.map((legend, index) => {
                  const key = Object.keys(legend)[0];
                  return (
                    <li
                      key={index}
                      className="text-[11.5px] leading-snug text-gray-900"
                    >
                      {key}. {legend[key]}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : (
          <NoPreview height={imgHeight} />
        )}
      </div>
      {col.figureNumber && (
        <p className="text-center text-[13px] text-gray-600 font-interMedium py-1 border-t border-dashed border-gray-200">
          {col.figureNumber.replace(/_/g, ". ")}
        </p>
      )}
    </div>
  );

  const accusedSection = (col: FeatureColumnData, imgHeight: string) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Same two-row layout as the patent header: caption + links, then the
          action buttons — avoids the cramped mid-word wrapping on ~330px
          cards. */}
      <div className="px-2.5 py-1.5 bg-[#F8FAFC] border-b border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
          <span className="text-[10px] font-interSemiBold tracking-wide uppercase text-[#667085] whitespace-nowrap">
            Accused Instrumentality
          </span>
          <span className="flex items-center gap-2.5 flex-shrink-0">
            {sourceLink(targetLink, "View Target")}
            {sourceLink(targetSnapshotLink, "Snapshot")}
          </span>
        </div>
        <div className="flex gap-1.5 mt-1.5">
          <button
            type="button"
            onClick={() => onAnnotate(col.key)}
            className={`edit-image-button ${actionBtnCls}`}
            style={actionBtnStyle}
          >
            <PencilIcon />
            Annotation
            <span className="loading-spinner"></span>
          </button>
          <button
            type="button"
            onClick={() => onReanalyze(col.key)}
            className={actionBtnCls}
            style={actionBtnStyle}
          >
            <RefreshIcon />
            ReAnalyze
          </button>
        </div>
      </div>
      <div className="bg-[#FAFBFC]">
        {col.accusedImageUrl ? (
          <ExpandableZoomableImage
            url={col.accusedImageUrl}
            alt={`Accused instrumentality — ${col.subFeature}`}
            title={`Accused Instrumentality — ${col.subFeature}`}
            height={imgHeight}
            link={targetLink}
            linkLabel="View Target"
            snapshotLink={targetSnapshotLink}
            onRotated={onImageRotated}
          />
        ) : (
          <NoPreview height={imgHeight} />
        )}
      </div>
    </div>
  );

  const infoSections = (col: FeatureColumnData, includeChartAndAnalysis: boolean) => (
    <>
      {/* Figure legend now renders beside the patent figure itself (same
          manner as the claim view's Claim Figures panel) — no separate box. */}

      {/* Single-feature claim chart (expanded modal only — the grid shows one
          common table for the whole element below the columns) */}
      {includeChartAndAnalysis && col.chartHtml && (
        <div
          className="feature-chart mt-2.5"
          dangerouslySetInnerHTML={{ __html: col.chartHtml }}
        />
      )}

      {/* Verdict */}
      {col.matchPercentage !== undefined && col.matchPercentage !== null && (
        <div className="mt-2.5 border border-gray-200 rounded-lg overflow-hidden text-[12px]">
          <div className="flex justify-between items-center px-2.5 py-1.5">
            <span className="text-[#667085] font-interMedium">Match percentage</span>
            <span
              className={`text-[10.5px] font-interSemiBold px-2 py-[1px] rounded-full border ${
                col.isMatched
                  ? "text-green-700 bg-green-50 border-green-200"
                  : "text-red-700 bg-red-50 border-red-200"
              }`}
            >
              {String(col.matchPercentage)}%
            </span>
          </div>
        </div>
      )}

      {/* Text analysis */}
      {includeChartAndAnalysis && col.analysisHtml && (
        <div className="mt-2.5 border border-gray-200 rounded-lg bg-[#F8FAFC] px-3 py-2">
          <p className="text-[10px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-1">
            Analysis — {col.subFeature}
          </p>
          <div
            className="text-[12.5px] text-[#475569] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: col.analysisHtml }}
          />
        </div>
      )}

    </>
  );

  return (
    <div>
      {/* Element clause node — hover previews live in MarkdownViewer's
          document-level listener (data-pf-col spans + .fig-ref chips).
          Omitted when the host already shows the clause (Features View). */}
      {clauseHtml && (
        <>
          <div className="w-full border border-gray-200 rounded-xl bg-[#F8FAFC] px-6 py-3 text-center">
            <p
              className="text-[13px] text-[#475569]"
              dangerouslySetInnerHTML={{ __html: clauseHtml }}
            />
          </div>

          <div className="text-center text-gray-400 text-lg leading-none my-2">
            ↓
          </div>
        </>
      )}

      {/* Columns — one horizontal strip, scrollable when features overflow */}
      {strip.overflowing && (
        <div className="flex items-center justify-end gap-2 mb-2">
          <span className="text-[11px] font-interMedium text-[#667085]">
            {n} features — scroll for more
          </span>
          <button
            type="button"
            title="Scroll left"
            onClick={() => scrollStrip(-1)}
            disabled={!strip.canLeft}
            className="p-0.5 rounded-full border border-gray-200 bg-white shadow-sm text-[#475569] hover:bg-gray-50 disabled:opacity-35 disabled:hover:bg-white"
            style={{ cursor: strip.canLeft ? "pointer" : "default" }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </button>
          <button
            type="button"
            title="Scroll right"
            onClick={() => scrollStrip(1)}
            disabled={!strip.canRight}
            className="p-0.5 rounded-full border border-gray-200 bg-white shadow-sm text-[#475569] hover:bg-gray-50 disabled:opacity-35 disabled:hover:bg-white"
            style={{ cursor: strip.canRight ? "pointer" : "default" }}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      )}
      <div className="relative">
        {strip.canLeft && (
          <>
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white to-transparent z-[5]" />
            <button
              type="button"
              title="Scroll left"
              onClick={() => scrollStrip(-1)}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-full shadow-md p-1 text-[#475569] hover:bg-gray-50"
              style={{ cursor: "pointer" }}
            >
              <ChevronLeftIcon sx={{ fontSize: 22 }} />
            </button>
          </>
        )}
        {strip.canRight && (
          <>
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white to-transparent z-[5]" />
            <button
              type="button"
              title="Scroll right"
              onClick={() => scrollStrip(1)}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-full shadow-md p-1 text-[#475569] hover:bg-gray-50"
              style={{ cursor: "pointer" }}
            >
              <ChevronRightIcon sx={{ fontSize: 22 }} />
            </button>
          </>
        )}
        <div
          ref={stripRef}
          onScroll={updateStrip}
          className={`flex gap-5 overflow-x-auto pb-2 [scrollbar-width:thin] ${
            strip.overflowing ? "" : "justify-center"
          }`}
        >
        {columns.map((col) => (
          <div
            key={col.key}
            data-col-key={col.key}
            className={`relative flex flex-col items-center flex-shrink-0 ${
              n === 1 ? "w-full" : ""
            }`}
            style={
              n === 1
                ? undefined
                : {
                    width:
                      dragWidthRef.current[col.key] ??
                      (colWidths[col.key] || 330),
                  }
            }
          >
            {/* Fixed-height pill slot so every card top aligns regardless of
                how many lines the pill label wraps to */}
            <div className="h-16 flex-shrink-0 flex items-center justify-center">
            <button
              type="button"
              onClick={() => onSelectColumn(col.key)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-[13px] font-interSemiBold transition-all ${
                col.isCurrent
                  ? "border-[#1751D0] text-[#1751D0] bg-[#F5F8FD] shadow-[0_0_0_3px_rgba(23,81,208,0.12)]"
                  : "border-gray-300 text-[#344054] bg-white hover:border-[#1751D0]"
              }`}
              // Same per-feature color as the clause banner / claim-structure
              // view; the current pill's border + ring pick it up too.
              style={
                col.hexCode
                  ? {
                      color: col.hexCode,
                      ...(col.isCurrent
                        ? {
                            borderColor: col.hexCode,
                            boxShadow: `0 0 0 3px ${col.hexCode}1F`,
                            background: `${col.hexCode}0D`,
                          }
                        : {}),
                    }
                  : undefined
              }
            >
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  col.isMatched ? "bg-green-500" : "bg-red-500"
                }`}
              />
              {col.subFeature}
            </button>
            </div>
            <div className="text-gray-400 text-lg leading-none my-1.5">↓</div>
            <div
              className={`w-full border-2 rounded-xl bg-white overflow-hidden flex-1 flex flex-col ${
                col.isMatched
                  ? col.isCurrent
                    ? "border-green-600 shadow-[0_0_0_4px_rgba(34,197,94,0.35)]"
                    : "border-green-500 shadow-[0_0_0_2px_rgba(34,197,94,0.22)]"
                  : col.isCurrent
                  ? "border-red-600 shadow-[0_0_0_4px_rgba(239,68,68,0.35)]"
                  : "border-red-500 shadow-[0_0_0_2px_rgba(239,68,68,0.22)]"
              }`}
            >
              {/* Expand to 80% modal */}
              <div className="flex justify-end px-1.5 pt-1.5 -mb-1">
                <button
                  type="button"
                  title="Expand"
                  onClick={() => onToggleExpand?.(col.key)}
                  className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                  style={{ cursor: "pointer" }}
                >
                  <OpenInFullIcon sx={{ fontSize: 15 }} />
                </button>
              </div>

              <div className="mx-3 mt-1">{patentSection(col, "14rem")}</div>

              {/* VS */}
              <div className="flex items-center gap-2.5 mx-4 my-2">
                <span className="flex-1 border-t border-dashed border-gray-200" />
                <span className="text-[10px] font-interSemiBold text-gray-400">VS</span>
                <span className="flex-1 border-t border-dashed border-gray-200" />
              </div>

              <div className="mx-3">{accusedSection(col, "14rem")}</div>

              <div className="mx-3 mb-3">{infoSections(col, false)}</div>
            </div>

            {/* Edge drag handles — resize this card's width from either side */}
            {n > 1 && (
              <>
                <div
                  onMouseDown={(e) => startCardResize(e, col.key, "left")}
                  className="absolute -left-1.5 top-16 bottom-0 w-2 z-10"
                  style={{ cursor: COL_RESIZE_CURSOR }}
                  title="Drag to resize"
                />
                <div
                  onMouseDown={(e) => startCardResize(e, col.key, "right")}
                  className="absolute -right-1.5 top-16 bottom-0 w-2 z-10"
                  style={{ cursor: COL_RESIZE_CURSOR }}
                  title="Drag to resize"
                />
              </>
            )}
          </div>
        ))}
        </div>
      </div>

      {/* Common claim chart — every feature's row pulled from its own item */}
      {commonChartHtml && (
        <div className="mt-7">
          <p className="text-[11px] font-interSemiBold uppercase tracking-wide text-[#667085] mb-1.5">
            Claim Chart — All Features
          </p>
          <div
            className="feature-chart feature-common-chart overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: commonChartHtml }}
          />
        </div>
      )}

      {/* Expanded 80% modal — restore via the icon or a backdrop click.
          Portaled so no ancestor stacking/overflow context can trap it. */}
      {expandedCol &&
        createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 1200 }}
          onClick={() => onToggleExpand?.(null)}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border-2 ${
              expandedCol.isMatched ? "border-green-500" : "border-red-500"
            }`}
            style={{ width: "80vw", height: "80vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
              <span className="inline-flex items-center gap-2 text-[14px] font-interSemiBold text-[#1A202C]">
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    expandedCol.isMatched ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                {expandedCol.subFeature}
              </span>
              <button
                type="button"
                title="Restore"
                onClick={() => onToggleExpand?.(null)}
                className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                style={{ cursor: "pointer" }}
              >
                <CloseFullscreenIcon sx={{ fontSize: 17 }} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {patentSection(expandedCol, "48vh")}
                {accusedSection(expandedCol, "48vh")}
              </div>
              {infoSections(expandedCol, true)}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* table styling for the single-feature charts */}
      <style>{`
        .feature-chart table { width: 100%; border-collapse: collapse; font-size: 12px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
        .feature-chart th { background: #f8fafc; text-align: left; font-size: 10px; letter-spacing: .04em; text-transform: uppercase; color: #667085; padding: 6px 9px; border-bottom: 1px solid #e5e7eb; }
        .feature-chart td { padding: 7px 9px; vertical-align: top; color: #334155; border-bottom: 1px solid #f1f5f9; }
        .feature-chart tr:last-child td { border-bottom: none; }
        .feature-chart tr.pf-current td { background: #F5F8FD; }
        .feature-chart .fig-ref { font-weight: 700; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 4px; padding: 0 4px; }
      `}</style>
    </div>
  );
};

export default PerFeatureAnalysisView;
